import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ColumnOptions } from '@shared';
import { MenuEvent, MenuSelectEvent } from '@progress/kendo-angular-menu';
import {
  ExportOption,
  ImportOptions,
} from 'src/app/restaurant-item/restaurant-item/constants';
@Component({
  selector: 'app-grid-manager-toolbar',
  templateUrl: './grid-manager-toolbar.component.html',
  styleUrl: './grid-manager-toolbar.component.scss',
})
export class GridManagerToolbarComponent {
  fontSizePopupVisible: boolean = false;
  columnsPopupVisible: boolean = false;
  configurationDialogVisible: boolean = false;

  @Input()
  showConfig: boolean = false;

  @Input()
  showImport: ImportOptions[] = ['Current Type'];

  @Input()
  showExport: ExportOption[] = ['Current Type', 'Current Type (All Tiers)', 'All' ];

  @Input()
  fontSize: number = 0.875;

  @Input()
  view!: string;

  @Input()
  columns: ColumnOptions[] = [];

  @Input()
  importUrl!: Map<ImportOptions, string> | string;

  currentImportUrl?: string;

  @ViewChild('tools', { static: true })
  toolsTemplate!: TemplateRef<any>;

  @ViewChild('tableFontSize', { static: true })
  tableFontSizeTemplate!: TemplateRef<any>;

  @ViewChild('tableColumns', { static: true })
  tableColumnsTemplate!: TemplateRef<any>;


  @ContentChild('configurationTemplate', { static: true })
  configurationTemplate!: TemplateRef<any>;

  menuItems: any[] = [];

  selectedItem: string = '';

  @Output()
  fontSizeChange: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  exportToExcel: EventEmitter<ExportOption> = new EventEmitter<ExportOption>();

  @Output()
  uploadSuccessful: EventEmitter<void> = new EventEmitter<void>();

  @Input()
  showDelivery: boolean = false;

  @Input()
  showBulkDelivery: boolean = true;

  /**
   * A locked tier has a sync on its way to the POS, so
   * fn_bulk_delivery_price_change refuses to touch it and answers zero items.
   * The option is disabled while that lasts, the same way Import is hidden.
   */
  @Input()
  tierLocked: boolean = false;

  @Output()
  openDeliverySettings: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  openBulkDeliveryChange: EventEmitter<void> = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges) {
    if (
      'showConfig' in changes ||
      'showImport' in changes ||
      'showExport' in changes ||
      'showExportAll' in changes ||
      'showViewExport' in changes ||
      'showDelivery' in changes ||
      'showBulkDelivery' in changes ||
      'tierLocked' in changes
    ) {
      this.setupMenuItems();
    }
  }

  setupMenuItems() {
    const toolItems: any[] = [];

    if (this.showDelivery) {
      toolItems.push(
        {
          text: 'Delivery Settings',
          cssClass: 'delivery-menu-item',
          data: {
            callbackFunction: () => this.openDeliverySettings.emit(),
            tooltip: 'Configure the upcharge % and rounding rules for this tier.',
          },
        },
        {
          text: 'Bulk Delivery % Change',
          cssClass: 'delivery-menu-item',
          disabled: !this.showBulkDelivery || this.tierLocked,
          data: {
            callbackFunction: () => this.openBulkDeliveryChange.emit(),
            tooltip: this.tierLocked
              ? 'The tier is locked while its changes are being synced to the POS. Bulk delivery changes are available once it unlocks.'
              : this.showBulkDelivery
                ? 'One-time price adjustment for delivery items.'
                : 'Available for A La Carte, Combo and Kids Meal only. Support for additional product types coming soon.',
          },
        },
      );
    }

    toolItems.push(
      {
        text: 'Table Text Size',
        contentTemplate: this.tableFontSizeTemplate,
      },
      {
        text: 'Show/Hide Columns',
        contentTemplate: this.tableColumnsTemplate,
      },
    );

    this.menuItems = [
      {
        text: 'Tools',
        template: this.toolsTemplate,
        cssClass: 'tools-menu-root',
        items: toolItems,
      },
    ];

    for (let option of this.showImport) {
      this.menuItems[0].items.push({
        text: `Import ${option}`,
        data: {
          callbackFunction: () => {
            if (this.importUrl instanceof Map && this.importUrl.has(option)) {
              this.currentImportUrl = this.importUrl.get(option);
            } else if (typeof this.importUrl === 'string') {
              this.currentImportUrl = this.importUrl;
            }
            this.uploadDialogVisible = true;
          },
        },
      });
    }
    for (let option of this.showExport) {
      this.menuItems[0].items.push({
        text: `Export ${option}`,
        data: {
          callbackFunction: () => this.exportToExcel.emit(option),
        },
      });
    }
  }

  uploadDialogVisible: boolean = false;
  changeFontSize(fontSize: number) {
    this.fontSizeChange.emit(fontSize);
  }

  onUploadComplete(event: any) {
    this.uploadSuccessful.emit();
  }

  onMenuSelect(event: MenuSelectEvent) {
    // A disabled item keeps its pointer events so its tooltip still shows on
    // hover, so the click has to be turned down here
    if (event.item.disabled) return;

    this.selectedItem = event.item.text;

    if (event.item.data?.callbackFunction) {
      event.item.data.callbackFunction();
    }
  }

  onMenuOpen(event: MenuEvent) {
    this.selectedItem = event.item.text;
    this.applyDeliveryTooltips();
  }

  private applyDeliveryTooltips() {
    setTimeout(() => {
      const items = document.querySelectorAll('.delivery-menu-item .k-link');
      items.forEach((link: Element) => {
        const text = link.textContent?.trim();
        const menuItem = this.menuItems[0]?.items?.find(
          (i: any) => i.text === text,
        );
        // Always rewrite it: the text of the tooltip depends on the state of the
        // tier and of the product type, so a stale one would explain the wrong thing.
        // The row carries it too, so the hover works anywhere on the item
        if (menuItem?.data?.tooltip) {
          link.setAttribute('title', menuItem.data.tooltip);
          link
            .closest('.delivery-menu-item')
            ?.setAttribute('title', menuItem.data.tooltip);
        }
      });
    });
  }

  onClose(event: MenuEvent) {
    if (this.selectedItem === 'Table Text Size') event.preventDefault();
  }
}
